import type {HTMLButtonAttributes} from 'svelte/elements';
import type {VariantProps} from 'tailwind-variants';
import {tv} from 'tailwind-variants';
import type {Class} from 'Catalyst/class';
import {type IconProps} from 'Catalyst/components/Icon';
import {
  buttonSlotsBaseResizingH,
  buttonSlotsBaseResizingW,
  buttonVariantsSize24dpBaseHeight,
  buttonVariantsSize32dpBaseHeight,
  buttonVariantsSize36dpBaseHeight,
  buttonVariantsSize40dpBaseHeight,
  buttonVariantsSize48dpBaseHeight,
  buttonVariantsSize56dpBaseHeight,
} from 'Catalyst/tokens/output/button';
import Button from './Button.svelte';

export const buttonTvProps = tv({
  slots: {
    base: `
      ${buttonSlotsBaseResizingH}
      ${buttonSlotsBaseResizingW}
      bg-transparent
      border-none
      rounded-[--button-slots-base-border-radius]
      focus-visible:outline
      focus-visible:outline-[length:--button-slots-base-outline-width]
      focus-visible:outline-[--button-slots-base-outline-color]
      focus-visible:outline-offset-[--button-slots-base-outline-offset]
      focus-within:ring-0
      [font:--button-slots-label-typography]
      !font-[number:--button-slots-label-font-weight]
    `,
    icon: '',
  },
  variants: {
    size: {
      '24dp': {
        base: `
          px-[--button-variants-size-24dp-base-px]
          h-[--button-variants-size-24dp-base-height]
          gap-[--button-variants-size-24dp-base-gap]
          [font:--button-variants-size-24dp-label-typography]
        `,
        icon: `
          density-[--button-variants-size-24dp-icon-density]
          size-[--button-variants-size-24dp-icon-size]
        `,
      },
      '32dp': {
        base: `
          px-[--button-variants-size-32dp-base-px]
          h-[--button-variants-size-32dp-base-height]
          gap-[--button-variants-size-32dp-base-gap]
          [font:--button-variants-size-32dp-label-typography] 
        `,
        icon: `
          density-[--button-variants-size-32dp-icon-density]
          size-[--button-variants-size-32dp-icon-size]
        `,
      },
      '36dp': {
        base: `
          px-[--button-variants-size-36dp-base-px]
          h-[--button-variants-size-36dp-base-height]
          gap-[--button-variants-size-36dp-base-gap]
          [font:--button-variants-size-36dp-label-typography]
        `,
        icon: `
          density-[--button-variants-size-36dp-icon-density]
          size-[--button-variants-size-36dp-icon-size]
        `,
      },
      '40dp': {
        base: `
          px-[--button-variants-size-40dp-base-px]
          h-[--button-variants-size-40dp-base-height]
          gap-[--button-variants-size-40dp-base-gap]
          [font:--button-variants-size-40dp-label-typography]
        `,
        icon: `
          density-[--button-variants-size-40dp-icon-density]
          size-[--button-variants-size-40dp-icon-size]
        `,
      },
      '48dp': {
        base: `
          px-[--button-variants-size-48dp-base-px]
          h-[--button-variants-size-48dp-base-height]
          gap-[--button-variants-size-48dp-base-gap]
          [font:--button-variants-size-48dp-label-typography]
        `,
        icon: `
          density-[--button-variants-size-48dp-icon-density]
          size-[--button-variants-size-48dp-icon-size]
        `,
      },
      '56dp': {
        base: `
          px-[--button-variants-size-56dp-base-px]
          h-[--button-variants-size-56dp-base-height]
          gap-[--button-variants-size-56dp-base-gap]
          [font:--button-variants-size-56dp-label-typography]
        `,
        icon: `
          density-[--button-variants-size-56dp-icon-density]
          size-[--button-variants-size-56dp-icon-size]
        `,
      },
    },
    style: {
      filled: '',
      outlined: {
        base: `
          outline
          outline-[length:--button-variants-style-outlined-base-outline-width]
          outline-offset-[--button-variants-style-outlined-base-outline-offset]
        `,
      },
      tonal: '',
      text: '',
    },
    semantic: {
      brand: '',
      info: '',
      error: '',
      warning: '',
      success: '',
      neutral: '',
    },
    disabled: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Filled + Brand
    {
      style: 'filled',
      semantic: 'brand',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-brand-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-brand-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-brand-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-brand-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-brand-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-brand-icon-glyph]
        `,
      },
    },
    // Filled + Info
    {
      style: 'filled',
      semantic: 'info',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-info-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-info-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-info-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-info-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-info-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-info-icon-glyph]
        `,
      },
    },
    // Filled + Error
    {
      style: 'filled',
      semantic: 'error',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-error-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-error-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-error-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-error-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-error-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-error-icon-glyph]
        `,
      },
    },
    // Filled + Warning
    {
      style: 'filled',
      semantic: 'warning',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-warning-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-warning-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-warning-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-warning-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-warning-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-warning-icon-glyph]
        `,
      },
    },
    // Filled + Success
    {
      style: 'filled',
      semantic: 'success',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-success-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-success-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-success-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-success-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-success-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-success-icon-glyph]
        `,
      },
    },
    // Filled + Neutral
    {
      style: 'filled',
      semantic: 'neutral',
      class: {
        base: `
          bg-[--button-compound-variants-style-filled-and-semantic-neutral-base-bg-color]
          hover:bg-[--button-compound-variants-style-filled-and-semantic-neutral-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-filled-and-semantic-neutral-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-filled-and-semantic-neutral-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-filled-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-filled-and-semantic-neutral-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-filled-and-semantic-neutral-icon-glyph]
        `,
      },
    },
    // Outlined + Brand
    {
      style: 'outlined',
      semantic: 'brand',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-brand-base-outline-color]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-brand-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-brand-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-brand-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-brand-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-brand-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-brand-icon-glyph]
        `,
      },
    },
    // Outlined + Info
    {
      style: 'outlined',
      semantic: 'info',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-info-base-outline-color]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-info-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-info-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-info-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-info-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-info-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-info-icon-glyph]
        `,
      },
    },
    // Outlined + Error
    {
      style: 'outlined',
      semantic: 'error',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-error-base-outline-color]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-error-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-error-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-error-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-error-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-error-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-error-icon-glyph]
        `,
      },
    },
    // Outlined + Warning
    {
      style: 'outlined',
      semantic: 'warning',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-warning-base-outline-color]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-warning-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-warning-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-warning-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-warning-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-warning-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-warning-icon-glyph]
        `,
      },
    },
    // Outlined + Success
    {
      style: 'outlined',
      semantic: 'success',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-success-base-outline-color]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-success-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-success-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-success-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-success-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-success-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-success-icon-glyph]
        `,
      },
    },
    // Outlined + Neutral
    {
      style: 'outlined',
      semantic: 'neutral',
      class: {
        base: `
          outline-[--button-compound-variants-style-outlined-and-semantic-neutral-base-outline-color]
          focus-within:outline-[--button-compound-variants-style-outlined-and-semantic-neutral-base-outline-color]
          focus-within:outline-[length:--button-variants-style-outlined-base-outline-width]
          focus-within:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          hover:bg-[--button-compound-variants-style-outlined-and-semantic-neutral-and-state-hover-base-bg-color]
          hover:outline-[length:--button-variants-style-outlined-base-outline-width]
          hover:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          focus-visible:bg-[--button-compound-variants-style-outlined-and-semantic-neutral-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-outlined-and-semantic-neutral-and-state-active-base-bg-color]
          active:outline-[length:--button-variants-style-outlined-base-outline-width]
          active:outline-offset-[--button-variants-style-outlined-base-outline-offset]
          disabled:outline-[--button-compound-variants-style-outlined-and-state-disabled-base-outline-color]
          text-[--button-compound-variants-style-outlined-and-semantic-neutral-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-outlined-and-semantic-neutral-icon-glyph]
        `,
      },
    },
    // Tonal + Brand
    {
      style: 'tonal',
      semantic: 'brand',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-brand-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-brand-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-brand-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-brand-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-brand-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-brand-icon-glyph]
        `,
      },
    },
    // Tonal + Info
    {
      style: 'tonal',
      semantic: 'info',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-info-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-info-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-info-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-info-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-info-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-info-icon-glyph]
        `,
      },
    },
    // Tonal + Error
    {
      style: 'tonal',
      semantic: 'error',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-error-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-error-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-error-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-error-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-error-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-error-icon-glyph]
        `,
      },
    },
    // Tonal + Warning
    {
      style: 'tonal',
      semantic: 'warning',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-warning-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-warning-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-warning-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-warning-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-warning-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-warning-icon-glyph]
        `,
      },
    },
    // Tonal + Success
    {
      style: 'tonal',
      semantic: 'success',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-success-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-success-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-success-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-success-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-success-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-success-icon-glyph]
        `,
      },
    },
    // Tonal + Neutral
    {
      style: 'tonal',
      semantic: 'neutral',
      class: {
        base: `
          bg-[--button-compound-variants-style-tonal-and-semantic-neutral-base-bg-color]
          hover:bg-[--button-compound-variants-style-tonal-and-semantic-neutral-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-tonal-and-semantic-neutral-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-tonal-and-semantic-neutral-and-state-active-base-bg-color]
          disabled:bg-[--button-compound-variants-style-tonal-and-state-disabled-base-bg-color]
          text-[--button-compound-variants-style-tonal-and-semantic-neutral-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-tonal-and-semantic-neutral-icon-glyph]
        `,
      },
    },
    // Text + Brand
    {
      style: 'text',
      semantic: 'brand',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-brand-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-brand-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-brand-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-brand-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-brand-icon-glyph]
        `,
      },
    },
    // Text + Info
    {
      style: 'text',
      semantic: 'info',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-info-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-info-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-info-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-info-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-info-icon-glyph]
        `,
      },
    },
    // Text + Error
    {
      style: 'text',
      semantic: 'error',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-error-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-error-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-error-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-error-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-error-icon-glyph]
        `,
      },
    },
    // Text + Warning
    {
      style: 'text',
      semantic: 'warning',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-warning-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-warning-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-warning-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-warning-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-warning-icon-glyph]
        `,
      },
    },
    // Text + Success
    {
      style: 'text',
      semantic: 'success',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-success-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-success-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-success-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-success-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-success-icon-glyph]
        `,
      },
    },
    // Text + Neutral
    {
      style: 'text',
      semantic: 'neutral',
      class: {
        base: `
          hover:bg-[--button-compound-variants-style-text-and-semantic-neutral-and-state-hover-base-bg-color]
          focus-visible:bg-[--button-compound-variants-style-text-and-semantic-neutral-and-state-focus-base-bg-color]
          active:bg-[--button-compound-variants-style-text-and-semantic-neutral-and-state-active-base-bg-color]
          text-[--button-compound-variants-style-text-and-semantic-neutral-label-color]
        `,
        icon: `
          text-[--button-compound-variants-style-text-and-semantic-neutral-icon-glyph]
        `,
      },
    },

    // Disabled compound variants - these override semantic colors with !important
    {
      style: 'filled',
      disabled: true,
      class: {
        base: '!text-[--button-compound-variants-style-filled-and-state-disabled-label-color]',
        icon: '!text-[--button-compound-variants-style-filled-and-state-disabled-icon-glyph]',
      },
    },
    {
      style: 'outlined',
      disabled: true,
      class: {
        base: '!text-[--button-compound-variants-style-outlined-and-state-disabled-label-color]',
        icon: '!text-[--button-compound-variants-style-outlined-and-state-disabled-icon-glyph]',
      },
    },
    {
      style: 'tonal',
      disabled: true,
      class: {
        base: '!text-[--button-compound-variants-style-tonal-and-state-disabled-label-color]',
        icon: '!text-[--button-compound-variants-style-tonal-and-state-disabled-icon-glyph]',
      },
    },
    {
      style: 'text',
      disabled: true,
      class: {
        base: '!text-[--button-compound-variants-style-text-and-state-disabled-label-color]',
        icon: '!text-[--button-compound-variants-style-text-and-state-disabled-icon-glyph]',
      },
    },
  ],
  defaultVariants: {
    size: '36dp',
    style: 'filled',
    semantic: 'brand',
    disabled: false,
  },
});

export type ButtonProps = HTMLButtonAttributes & {
  label?: string;
  leadingIcon?: IconProps['name'];
  trailingIcon?: IconProps['name'];
  size?: VariantProps<typeof buttonTvProps>['size'] | 'xs' | 'sm' | 'md' | 'lg';
  style?: VariantProps<typeof buttonTvProps>['style'];
  semantic?: VariantProps<typeof buttonTvProps>['semantic'];
  disabled?: boolean;
  href?: string;
  target?: string;
  class?: Class;
  labelClass?: {class?: string};
};

export {Button};
